import React, { useState, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon, LightningBoltIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/outline';
import { apiGenerateDemoReferrals, apiBackfillIntakeConcern } from '../../utils/rmsApi';

/**
 * GenerateDemoReferralsDialog
 * ----------------------------
 * DEV/TESTING ONLY dialog for batch-generating demo patient records across the
 * various referral stages shown on the provider dashboard.
 *
 * The clinician can:
 *   1. Toggle which stages to populate (all selected by default).
 *   2. Set how many records to generate per stage (default 5, range 1-20).
 *   3. Click "Generate" to trigger the rmsGenerateDemoReferrals Cloud Function.
 *
 * After generation, the dashboard's existing Firestore onSnapshot listener
 * automatically picks up the new records — no manual refresh needed.
 *
 * Props:
 *   isOpen       – boolean controlling dialog visibility
 *   onOpenChange – (open: boolean) => void, called when dialog should open/close
 *   orgId        – the current organisation ID for Firestore writes
 */

// Stage definitions matching the REFERRAL_STAGES in RmsProviderDashboard.js
// and the VALID_STAGE_IDS in the backend rms_demo.py.
const STAGE_OPTIONS = [
  { id: 'incomplete-referral-forms', label: 'Incomplete Referral Forms', group: 'Incoming Referrals' },
  { id: 'completed-referral-forms', label: 'Completed Referral Forms', group: 'Incoming Referrals' },
  { id: 'incomplete-triage-forms', label: 'Incomplete Triage Forms', group: 'Incoming Referrals' },
  { id: 'pending-1st-review', label: 'Pending 1st Review', group: 'Ready for Triage' },
  { id: 'pending-2nd-review', label: 'Second Review', group: 'Ready for Triage' },
  { id: 'waiting-list', label: 'Waiting List', group: 'Triage Complete' },
  { id: 'discharged-referrals', label: 'Not Accepted at Triage', group: 'Triage Complete' },
];

const DEFAULT_COUNT = 5;

export default function GenerateDemoReferralsDialog({ isOpen, onOpenChange, orgId }) {
  // Track which stages are selected (all on by default) and the count per stage
  const [selectedStages, setSelectedStages] = useState(() => {
    const initial = {};
    STAGE_OPTIONS.forEach((s) => { initial[s.id] = true; });
    return initial;
  });

  const [counts, setCounts] = useState(() => {
    const initial = {};
    STAGE_OPTIONS.forEach((s) => { initial[s.id] = DEFAULT_COUNT; });
    return initial;
  });

  // UI state machine: 'config' → 'generating' → 'success' | 'error'
  const [uiState, setUiState] = useState('config');
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const toggleStage = useCallback((stageId) => {
    setSelectedStages((prev) => ({ ...prev, [stageId]: !prev[stageId] }));
  }, []);

  const toggleAll = useCallback(() => {
    const allSelected = STAGE_OPTIONS.every((s) => selectedStages[s.id]);
    const newValue = !allSelected;
    setSelectedStages(() => {
      const next = {};
      STAGE_OPTIONS.forEach((s) => { next[s.id] = newValue; });
      return next;
    });
  }, [selectedStages]);

  const updateCount = useCallback((stageId, value) => {
    const num = Math.max(0, Math.min(20, parseInt(value, 10) || 0));
    setCounts((prev) => ({ ...prev, [stageId]: num }));
  }, []);

  const totalRecords = STAGE_OPTIONS.reduce(
    (sum, s) => sum + (selectedStages[s.id] ? (counts[s.id] || DEFAULT_COUNT) : 0),
    0,
  );

  const handleGenerate = useCallback(async () => {
    if (!orgId || totalRecords === 0) return;

    const stages = STAGE_OPTIONS
      .filter((s) => selectedStages[s.id] && (counts[s.id] || 0) > 0)
      .map((s) => ({ stageId: s.id, count: counts[s.id] || DEFAULT_COUNT }));

    if (stages.length === 0) return;

    setUiState('generating');
    setErrorMessage(null);

    try {
      const data = await apiGenerateDemoReferrals({ orgId, stages });
      setResult(data);
      setUiState('success');
    } catch (err) {
      console.error('[GenerateDemoReferrals] Error:', err);
      setErrorMessage(err?.message || 'Failed to generate demo referrals');
      setUiState('error');
    }
  }, [orgId, selectedStages, counts, totalRecords]);

  // DEV: classify existing demo patients that have no intakeConcern so they
  // appear under the per-pathway ADHD / autism triage tabs.
  const handleBackfill = useCallback(async () => {
    if (!orgId) return;
    setUiState('generating');
    setErrorMessage(null);
    try {
      const data = await apiBackfillIntakeConcern({ orgId });
      setResult({ backfill: true, ...data });
      setUiState('success');
    } catch (err) {
      console.error('[GenerateDemoReferrals] Backfill error:', err);
      setErrorMessage(err?.message || 'Failed to backfill intake concern');
      setUiState('error');
    }
  }, [orgId]);

  // Reset state when the dialog is opened/closed
  const handleOpenChange = useCallback((open) => {
    if (!open) {
      // Reset to config state when closing, with a slight delay so the close
      // animation finishes before the content shifts.
      setTimeout(() => {
        setUiState('config');
        setResult(null);
        setErrorMessage(null);
      }, 200);
    }
    onOpenChange(open);
  }, [onOpenChange]);

  // Group stages by their section heading
  const groups = [];
  let currentGroup = null;
  STAGE_OPTIONS.forEach((s) => {
    if (!currentGroup || currentGroup.label !== s.group) {
      currentGroup = { label: s.group, stages: [] };
      groups.push(currentGroup);
    }
    currentGroup.stages.push(s);
  });

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <Dialog.Content className="radix-dialog-content-centered-motion fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-[540px] max-h-[85vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <LightningBoltIcon className="w-5 h-5 text-amber-500" />
              <Dialog.Title className="text-lg font-bold text-gray-900">
                Generate Demo Referrals
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {uiState === 'config' && (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Select which referral stages to populate with demo patient records.
                  Records will appear in the dashboard automatically via real-time sync.
                </p>

                {/* Select all toggle */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="text-xs font-medium text-primary hover:text-primary/80"
                  >
                    {STAGE_OPTIONS.every((s) => selectedStages[s.id]) ? 'Deselect all' : 'Select all'}
                  </button>
                  <span className="text-xs text-gray-500">
                    {totalRecords} record{totalRecords !== 1 ? 's' : ''} total
                  </span>
                </div>

                {/* Stage list grouped by section */}
                <div className="space-y-4">
                  {groups.map((group) => (
                    <div key={group.label}>
                      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                        {group.label}
                      </h4>
                      <div className="space-y-2">
                        {group.stages.map((stage) => (
                          <div
                            key={stage.id}
                            className={`flex items-center justify-between rounded-lg border p-3 transition-colors ${
                              selectedStages[stage.id]
                                ? 'border-primary/30 bg-primary/5'
                                : 'border-gray-200 bg-white'
                            }`}
                          >
                            <label className="flex items-center gap-3 flex-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedStages[stage.id]}
                                onChange={() => toggleStage(stage.id)}
                                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary/50"
                              />
                              <span className="text-sm font-medium text-gray-800">
                                {stage.label}
                              </span>
                            </label>

                            {/* Count input — only interactive when stage is selected */}
                            <div className="flex items-center gap-1.5">
                              <input
                                type="number"
                                min={0}
                                max={20}
                                value={counts[stage.id]}
                                onChange={(e) => updateCount(stage.id, e.target.value)}
                                disabled={!selectedStages[stage.id]}
                                className={`w-14 text-center text-sm border rounded-md py-1 focus:outline-none focus:ring-1 focus:ring-primary ${
                                  selectedStages[stage.id]
                                    ? 'border-gray-300 text-gray-800'
                                    : 'border-gray-200 text-gray-400 bg-gray-50'
                                }`}
                              />
                              <span className="text-xs text-gray-500 w-8">each</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {uiState === 'generating' && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mb-4" />
                <p className="text-sm font-medium text-gray-700">
                  Generating {totalRecords} demo referral{totalRecords !== 1 ? 's' : ''}...
                </p>
                <p className="text-xs text-gray-500 mt-1">This usually takes 2-4 seconds.</p>
              </div>
            )}

            {uiState === 'success' && result && result.backfill && (
              <div className="flex flex-col items-center py-8 text-center">
                <CheckCircleIcon className="w-12 h-12 text-green-500 mb-4" />
                <p className="text-lg font-bold text-gray-900 mb-2">
                  {result.updated} patient{result.updated !== 1 ? 's' : ''} classified
                </p>
                <p className="text-sm text-gray-600">
                  Scanned {result.scanned} record{result.scanned !== 1 ? 's' : ''}; assigned an
                  intake concern to {result.updated} that had none. They should now appear under
                  the ADHD / Autism triage tabs.
                </p>
              </div>
            )}

            {uiState === 'success' && result && !result.backfill && (
              <div className="flex flex-col items-center py-8">
                <CheckCircleIcon className="w-12 h-12 text-green-500 mb-4" />
                <p className="text-lg font-bold text-gray-900 mb-2">
                  {result.totalCreated} referral{result.totalCreated !== 1 ? 's' : ''} created
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Written in {result.batchesCommitted} batch{result.batchesCommitted !== 1 ? 'es' : ''}.
                  Records should appear in the dashboard momentarily.
                </p>

                {/* Per-stage breakdown */}
                <div className="w-full max-w-xs space-y-1.5">
                  {Object.entries(result.created || {}).map(([stageId, count]) => {
                    const stage = STAGE_OPTIONS.find((s) => s.id === stageId);
                    return (
                      <div key={stageId} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{stage?.label || stageId}</span>
                        <span className="font-semibold text-gray-900">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {uiState === 'error' && (
              <div className="flex flex-col items-center py-8">
                <ExclamationCircleIcon className="w-12 h-12 text-red-500 mb-4" />
                <p className="text-lg font-bold text-gray-900 mb-2">Generation failed</p>
                <p className="text-sm text-red-600 text-center">{errorMessage}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
            {uiState === 'config' && (
              <>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                </Dialog.Close>
                <button
                  type="button"
                  onClick={handleBackfill}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Assign an ADHD/ASD/both concern to existing patients that have none, so they show under the triage tabs."
                >
                  Backfill intake concern
                </button>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={totalRecords === 0}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LightningBoltIcon className="w-4 h-4" />
                  Generate {totalRecords} referral{totalRecords !== 1 ? 's' : ''}
                </button>
              </>
            )}

            {(uiState === 'success' || uiState === 'error') && (
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
              </Dialog.Close>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
